import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Archive } from 'lucide-react';
import { format } from 'date-fns';
import type { Client, ClientStatus } from '@/types/entities';
import { useNavigate } from 'react-router-dom';

interface ClientTableProps {
  clients: Client[];
}

const statusConfig: Record<
  ClientStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  lead: { label: 'Lead', variant: 'outline' },
  active: { label: 'Active', variant: 'default' },
  inactive: { label: 'Inactive', variant: 'secondary' },
  archived: { label: 'Archived', variant: 'destructive' },
};

export function ClientTable({ clients }: ClientTableProps) {
  const navigate = useNavigate();

  if (clients.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No clients found. Try adjusting your filters.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Company Name</TableHead>
            <TableHead>Contact Person</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Revenue</TableHead>
            <TableHead>Projects</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client) => (
            <TableRow
              key={client.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <TableCell className="font-medium">{client.companyName}</TableCell>
              <TableCell>{client.contactPerson}</TableCell>
              <TableCell className="text-gray-600">{client.email}</TableCell>
              <TableCell>
                <Badge variant={statusConfig[client.status].variant}>
                  {statusConfig[client.status].label}
                </Badge>
              </TableCell>
              <TableCell className="capitalize">{client.source}</TableCell>
              <TableCell>${client.totalRevenue.toLocaleString()}</TableCell>
              <TableCell>
                {client.activeProjects} / {client.totalProjects}
              </TableCell>
              <TableCell className="text-gray-600">
                {format(new Date(client.createdAt), 'MMM d, yyyy')}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${client.id}`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${client.id}/edit`);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Archive client:', client.id);
                      }}
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
